import {
  FormattedLine,
  FormattedSegmentsOptions,
  FormattedSegmentsResult,
  FormattedTextResult,
  PageSize,
  TextFormattingOptions,
  TextSegment,
  TitleOptions,
} from "../types/pdf-generator";
import { drawHorizontalLine } from "./graphicDraw";
import RulesLinker from "./rulesLinker";
import TextParser from "./textParser";
import { cleanTextForPdf, truncateText } from "./textUtils";

// Initialize pdf-lib functions
let PDFDocument: any;
let rgb: any;

// Load pdf-lib functions synchronously (for compatibility)
function initializePDFLib() {
  if (!PDFDocument || !rgb) {
    // Try to get from window first (production)
    if (window.PDFLib) {
      PDFDocument = window.PDFLib.PDFDocument;
      rgb = window.PDFLib.rgb;
    } else {
      // Fallback to default values (will be set later)
      console.warn("PDFLib not available, using fallback values");
    }
  }
}

// Type definitions for pdf-lib
type PDFFont = any;
type PDFPage = any;

class TextWriter {
  private textParser: TextParser;
  private trace: boolean = false;
  private pdfDoc: typeof PDFDocument;
  private rulesLinker: RulesLinker;
  private pageSize: PageSize;

  constructor(
    pdfDoc: typeof PDFDocument,
    pageSize: PageSize,
    standardFont: PDFFont,
    rulesLinker: RulesLinker
  ) {
    this.pdfDoc = pdfDoc;
    this.textParser = new TextParser(standardFont);
    this.trace = false;
    this.pageSize = pageSize;
    this.rulesLinker = rulesLinker;
  }

  stripHtml(html: string): string {
    const processedHtml = html.replace(/\*/g, "\\*").replace(/_/g, "\\_");
    const text = this.textParser.parseHtmlContent(processedHtml);
    return cleanTextForPdf(text);
  }

  stripHtmlWithoutExamples(html: string): string {
    const result = this.textParser.removeAnchors(html);
    return this.stripHtml(result);
  }

  setTrace(trace: boolean): void {
    this.trace = trace;
    this.textParser.setTrace(trace);
  }

  getTrace(): boolean {
    return this.trace;
  }

  setId(id: string): void {
    this.textParser.setId(id);
  }

  /**
   * Aligner du texte au centre
   * @param text le texte à aligner
   * @param font la police de caractères
   * @param fontSize la taille de la police
   * @param width la largeur disponible
   * @returns  le nombre de pixels à ajouter à gauche pour aligner le texte au centre
   */
  alignCenter(
    text: string,
    font: PDFFont,
    fontSize: number,
    width: number
  ): number {
    return (width - font.widthOfTextAtSize(text, fontSize)) / 2;
  }

  /**
   * calcul la longueur du texte en tenant compte des retours à la ligne
   */
  calculateTextWidth(text: string, font: PDFFont, fontSize: number): number {
    if (!text.includes("\n")) {
      return font.widthOfTextAtSize(text, fontSize);
    }

    const tmpTitle = text.replace("\n", "");
    return font.widthOfTextAtSize(tmpTitle, fontSize);
  }

  /**
   * draw a title with a break point if needed
   */
  drawTitle(
    title: string,
    {
      page,
      x,
      y,
      font,
      size,
      maxWidth,
      lineSpacing = 1,
      align,
      color,
      opacity = 1,
    }: TitleOptions
  ): number {
    // Initialize pdf-lib if needed
    initializePDFLib();

    // Use loaded rgb function
    const titleColor = color || rgb(0, 0, 0);
    let titleWidth = this.calculateTextWidth(title, font, size);

    if (title.includes("\n") || titleWidth > maxWidth) {
      // Try to break on period or comma
      const breakPointsPriority = ["\n", ".", ",", ":", ";", "!", " "];

      const breakPoints: number[] = [];
      let found = false;

      if (titleWidth >= maxWidth * 1.95) {
        // le titre fait 2 fois la largeur max, on tronque !
        title = truncateText(title, maxWidth * 1.9, font, size);
        titleWidth = this.calculateTextWidth(title, font, size);

        if (this.trace) {
          console.log("cut title", title, titleWidth);
        }
      }
      title = title.replace("...", "__");

      const overflow = Math.min(0.4, (titleWidth - maxWidth) / titleWidth);

      const charWidth = titleWidth / title.length;
      const maxLength = Math.floor(maxWidth / charWidth);

      const min = Math.floor(title.length * overflow);
      const max = Math.floor(
        Math.min(title.length * (1 - overflow), maxLength)
      );
      let withBreakPoint = 0;

      for (const breakPoint of breakPointsPriority) {
        for (let i = min; i < max; i++) {
          if (title[i] === breakPoint) {
            breakPoints.push(i);
            found = true;
          }
        }
        if (found) {
          if (breakPoint !== "\n") {
            withBreakPoint = 1;
          }
          break;
        }
      }

      title = title.replace("__", "...");

      if (breakPoints.length > 0) {
        if (this.trace) {
          console.log("breakPoints for", title);
        }
        // Find the best break point (closest to middle)
        const middle = Math.floor(title.length / 2);
        let bestBreak = breakPoints[0];
        let minDistance = Math.abs(bestBreak - middle);
        let currentBreak = 0;

        for (let i = 0; i < breakPoints.length; i++) {
          const breakPoint = breakPoints[i];
          const distance = Math.abs(breakPoint - middle);
          if (distance < minDistance) {
            minDistance = distance;
            bestBreak = breakPoint;
            currentBreak = i;
          }
        }

        let firstLine = title.substring(0, bestBreak + withBreakPoint);

        if (this.trace) {
          console.log(
            "firstLine",
            firstLine,
            "currentBreak",
            currentBreak,
            "breakPoints",
            breakPoints
          );
        }

        while (
          font.widthOfTextAtSize(firstLine, size) >= maxWidth * 0.95 &&
          currentBreak > 0
        ) {
          currentBreak--;
          bestBreak = breakPoints[currentBreak];
          firstLine = title.substring(0, bestBreak + withBreakPoint);
        }

        let secondLine = title.substring(bestBreak + 1).trim();
        let secondLineWidth = font.widthOfTextAtSize(secondLine, size);

        if (this.trace) {
          console.log(
            "secondLine",
            secondLine,
            "secondLineWidth",
            secondLineWidth,
            "maxWidth",
            maxWidth
          );
        }

        if (secondLineWidth >= maxWidth * 0.95) {
          secondLine = truncateText(secondLine, maxWidth * 0.95, font, size);
          secondLineWidth = font.widthOfTextAtSize(secondLine, size);
        }

        // Draw first line
        const x1 =
          x +
          (align === "center"
            ? this.alignCenter(firstLine, font, size, maxWidth)
            : 0);

        page.drawText(firstLine, {
          x: x1,
          y: y,
          size: size,
          font: font,
          color: titleColor,
          opacity: opacity,
          maxWidth: maxWidth,
        });

        y -= size * lineSpacing;

        // Draw second line
        const x2 =
          x +
          (align === "center"
            ? this.alignCenter(secondLine, font, size, maxWidth)
            : maxWidth - secondLineWidth);

        page.drawText(secondLine, {
          x: x2,
          y: y,
          size: size,
          font: font,
          color: titleColor,
          opacity: opacity,
          maxWidth: maxWidth,
        });

        return y;
      } else {
        title = truncateText(title, maxWidth * 0.95, font, size) + "...";
        console.log("no break point found for", title);
      }
    }

    const x1 =
      x +
      (align === "center" ? this.alignCenter(title, font, size, maxWidth) : 0);

    page.drawText(title, {
      x: x1,
      y: y,
      size: size,
      font: font,
      color: titleColor,
      opacity: opacity,
      maxWidth: maxWidth,
    });
    return y;
  }

  /**
   * Dessine du texte formaté avec gestion du retour à la ligne
   */
  calculateFormattedTextWithWrap(
    text: string,
    options: TextFormattingOptions
  ): FormattedTextResult {
    const { fontSize, fonts, maxWidth, lineSpacing, textAlign, shiftSize } =
      options;

    // Nettoyer le texte d'entrée
    const cleanInputText = text
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .replace(/\t/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/[ ]{2,}/g, " ")
      .trim();

    let totalHeight = 0;
    let listSegments: FormattedLine[] = [];
    let firstSegmentsHeight = 0;
    let previousPaddingBottom = 0;

    if (cleanInputText.trim()) {
      // Utiliser wrapText pour découper la ligne en plusieurs lignes si nécessaire
      const wrappedLines = this.textParser.wrapText(cleanInputText);

      if (this.trace) {
        console.log("wrappedLines:", wrappedLines);
      }

      wrappedLines.forEach((wrappedLine) => {
        let wrappedContent = wrappedLine.content;
        if (!wrappedContent.trim()) return;

        // Calculer la ligne avec formatage
        const segmentsLine = this.textParser.calculateFormattedLine(
          wrappedLine,
          {
            fontSize,
            fonts,
            maxWidth,
            lineSpacing,
            textAlign,
            shiftSize,
          }
        );
        /**
         * ajoutons les padding si nécessaire, sauf pour les lignes vides
         */
        if (segmentsLine.segments.length > 0) {
          const padding = Math.max(
            wrappedLine.paddingTop,
            previousPaddingBottom
          );
          segmentsLine.height += padding;
          segmentsLine.paddingTop = padding;
        }
        previousPaddingBottom = wrappedLine.paddingBottom;

        listSegments.push(segmentsLine);
        totalHeight += segmentsLine.height;
      });
    }

    firstSegmentsHeight = listSegments[0]?.height || 0;
    return { listSegments, totalHeight, firstSegmentsHeight };
  }

  /**
   * Dessine les segments sur la page
   */
  drawSegments(
    page: PDFPage,
    segments: TextSegment[],
    x: number,
    y: number
  ): void {
    // Initialize pdf-lib if needed
    initializePDFLib();
    const currentX = x;
    const currentY = y;

    segments.forEach((segment) => {
      const { text: segmentText, x: segX, y: segY, size, font } = segment;

      if (!segmentText) return;

      const sx = Math.round(currentX + (segX || 0));
      const sy = Math.round(currentY - (segY || 0));

      // Dessiner le texte
      page.drawText(segmentText, {
        x: sx,
        y: sy,
        size: size || 12,
        font: font,
        color: rgb(0, 0, 0),
      });

      let textWidth = 50;
      let spaceWidth = 5;
      // Ajouter un soulignement si nécessaire
      if ((segment.underline || segment.link) && font && size) {
        textWidth = Math.round(font.widthOfTextAtSize(segmentText, size));
        spaceWidth = Math.round(font.widthOfTextAtSize(" ", size));
      }
      // Ajouter un soulignement si nécessaire
      if (segment.underline) {
        page.drawLine({
          start: { x: sx - spaceWidth / 2, y: sy - 2 },
          end: { x: sx + textWidth + spaceWidth / 2, y: sy - 2 },
          thickness: 0.5,
          color: rgb(0.1, 0.1, 0.1),
        });
      }
      // ajouter un lien si link est true
      if (segment.link && segment.linkId) {
        const newLink = {
          page: this.pdfDoc.getPages().indexOf(page) + 1,
          x: sx - spaceWidth / 2,
          y: sy,
          width: textWidth + spaceWidth,
          height: size || 12,
          ruleId: segment.linkId,
          withUnderline: true,
        };
        if (this.trace) {
          console.log("newLink", newLink);
        }
        this.rulesLinker.addRuleToLink(newLink);
      }
    });
  }

  /**
   * Dessine les segments formatés
   */
  drawFormattedSegments(
    page: PDFPage,
    listSegments: FormattedLine[],
    options: FormattedSegmentsOptions,
    addPage: Function
  ): FormattedSegmentsResult {
    // Initialize pdf-lib if needed
    initializePDFLib();
    const { x, y, canAddPage = true, bottomOffset = 0, fontSize } = options;
    let marginX = x;
    let isNewPage = false;
    let verticalOffset = 0;

    let currentY = Math.round(y * 10) / 10;

    for (const segment of listSegments) {
      verticalOffset = 0;
      if (!isNewPage && segment.paddingTop > 0) {
        verticalOffset = segment.paddingTop;
        currentY -= verticalOffset;

        if (this.trace) {
          console.log("paddingTop", segment.paddingTop, "segment", segment);
          console.log("currentY", currentY);
        }
      }
      let bottomSegment =
        Math.round(
          (currentY - (segment.height - fontSize) - bottomOffset) * 10
        ) / 10;
      if (this.trace) {
        drawHorizontalLine({
          page,
          x: x,
          y: bottomSegment,
          width: page.getWidth() - 2 * x,
          color: rgb(0, 0, 0.8), // Blue
        });
      }

      isNewPage = false;

      // Vérifier si on a besoin d'une nouvelle page
      if (canAddPage && bottomSegment < this.pageSize.marginBottom) {
        const { page: newPage, y, x } = addPage();
        page = newPage;
        marginX = x;
        currentY = y - fontSize;
        isNewPage = true;

        if (this.trace) {
          console.log(
            bottomSegment < this.pageSize.marginBottom
              ? "new page"
              : "NO new page",
            " for ",
            this.textParser.id,
            "currentY",
            currentY,
            "height ",
            Math.round(segment.height * 10) / 10,

            "bottom : ",
            bottomSegment,
            "segments",
            segment,
            "bottomMargin",
            this.pageSize.marginBottom,
            "bottomOffset",
            bottomOffset,
            "fontSize",
            fontSize
          );
        }
      }

      this.drawSegments(page, segment.segments, marginX, currentY);
      currentY -= segment.height - verticalOffset;
    }

    return { currentY, currentPage: page, currentX: marginX };
  }
}

export default TextWriter;
